import { Container, Text, TextStyle, Graphics, Sprite, Texture } from "pixi.js";

/**
 * MatrixBackground - Creates Matrix digital rain effect
 * Low coupling: Independent class for Matrix background rendering
 */
export class MatrixBackground extends Container {
  // Performance-critical constants (moved to top for better optimization)
  private static readonly FONT_SIZE = 18;
  private static readonly CHARS = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789$+-*/=%"\'#&_(),.;:?!\\|{}<>[]^~ｦｱｳｴｵｶｷｸｹｺｻｼｽｾｿﾀﾁﾂﾃﾄﾅﾆﾇﾈﾉﾊﾋﾌﾍﾎﾏﾐﾑﾒﾓﾔﾕﾖﾗﾘﾙﾚﾛﾜﾝ';
  private static readonly FONT_FAMILY = 'Consolas, Courier New, monospace';
  private static readonly FONT_WEIGHT = 'bold';
  
  // Color constants
  private static readonly COLOR_BACKGROUND = 0x000000;
  private static readonly COLOR_DEFAULT_FILL = 0x00ff00;
  private static readonly COLOR_WHITE = 0xffffff;
  private static readonly COLOR_BRIGHT_GREEN = 0xc0ffc0;
  private static readonly COLOR_GREEN = 0x00ff00;
  private static readonly COLOR_DARK_GREEN = 0x008f00;
  
  // Stream animation constants
  private static readonly STREAM_ACTIVATION_PROBABILITY = 0.98;
  private static readonly STREAM_SPEED_MULTIPLIER = 0.05;
  private static readonly STREAM_SPEED_BASE = 0.08;
  private static readonly STREAM_LENGTH_RANGE = 20;
  private static readonly STREAM_LENGTH_BASE = 10;
  private static readonly STREAM_INITIAL_ACTIVE_PROBABILITY = 0.5;
  
  // Grid buffer constants
  private static readonly GRID_BUFFER_ROWS = 20;
  private static readonly SPRITE_BUFFER_ROWS = 5;
  
  // Brightness thresholds and values
  private static readonly BRIGHTNESS_MAX = 1.0;
  private static readonly BRIGHTNESS_BRIGHT_THRESHOLD = 0.9;
  private static readonly BRIGHTNESS_WHITE_THRESHOLD = 0.95;
  private static readonly BRIGHTNESS_BRIGHT_GREEN_THRESHOLD = 0.8;
  private static readonly BRIGHTNESS_GREEN_THRESHOLD = 0.5;
  private static readonly BRIGHTNESS_MID_BASE = 0.7;
  private static readonly BRIGHTNESS_MID_MULTIPLIER = 0.5;
  private static readonly BRIGHTNESS_DECAY = 0.98;
  private static readonly BRIGHTNESS_MIN = 0.05;
  
  // Bright cell threshold (j < 3)
  private static readonly BRIGHT_CELL_THRESHOLD = 3;
  
  // Alpha values
  private static readonly ALPHA_MAX = 1.0;
  private static readonly ALPHA_MID = 0.9;
  private static readonly ALPHA_DARK_MULTIPLIER = 1.5;
  
  // Character change constants
  // Set to change approximately once per second (at 60fps: ~1.67% chance per frame after age threshold)
  private static readonly CHAR_CHANGE_AGE_THRESHOLD = 10;
  private static readonly CHAR_CHANGE_PROBABILITY = 0.983;
  
  // Z-index constant
  private static readonly BACKGROUND_Z_INDEX = -1000;
  
  // Static texture cache for all characters (shared across instances)
  private static charTextureCache: Map<string, Texture> = new Map();
  private static textureCacheInitialized = false;
  
  private columns: number;
  private rows: number;
  private fontSize: number;
  private chars: string;
  private streams: Array<{
    x: number;
    y: number;
    speed: number;
    length: number;
    active: boolean;
  }>;
  private sprites: Sprite[][];
  private grid: Array<Array<{ char: string; brightness: number; age: number; lastFill?: number; lastAlpha?: number; lastCharChange?: number }>>;
  private backgroundGraphics: Graphics;
  private spriteContainer: Container;
  private backgroundWidth: number;
  private backgroundHeight: number;
  private visibleWidth: number;
  private visibleHeight: number;

  constructor(backgroundWidth: number, backgroundHeight: number, visibleWidth?: number, visibleHeight?: number) {
    super();
    this.name = "MatrixBackground";
    
    // Ensure container is visible and not culled
    this.visible = true;
    this.cullable = false; // Prevent culling so it always renders

    // Use static constants for performance
    this.fontSize = MatrixBackground.FONT_SIZE;
    this.chars = MatrixBackground.CHARS;
    
    // Store dimensions for background (full size with padding)
    this.backgroundWidth = backgroundWidth;
    this.backgroundHeight = backgroundHeight;
    
    // Use visible size for Matrix effect calculation, or fallback to background size
    this.visibleWidth = visibleWidth ?? backgroundWidth;
    this.visibleHeight = visibleHeight ?? backgroundHeight;
    
    // Create black background - use full size to cover entire area including padding
    this.backgroundGraphics = new Graphics();
    this.backgroundGraphics.rect(0, 0, backgroundWidth, backgroundHeight);
    this.backgroundGraphics.fill(MatrixBackground.COLOR_BACKGROUND);
    // Ensure background is at the bottom layer and covers full area
    this.backgroundGraphics.zIndex = MatrixBackground.BACKGROUND_Z_INDEX;
    this.backgroundGraphics.visible = true;
    // Position background at (0, 0) relative to container to ensure it fills correctly
    this.backgroundGraphics.x = 0;
    this.backgroundGraphics.y = 0;
    this.addChildAt(this.backgroundGraphics, 0);
    
    // Calculate columns and rows based on visible size (not padded size)
    // This prevents creating too many sprites
    this.columns = Math.floor(this.visibleWidth / this.fontSize);
    this.rows = Math.floor(this.visibleHeight / this.fontSize);
    
    // Mark texture cache as initialized (textures generated on-demand)
    if (!MatrixBackground.textureCacheInitialized) {
      MatrixBackground.textureCacheInitialized = true;
    }
    
    // Create optimized container for sprite rendering
    // Using texture caching provides significant performance benefits
    this.spriteContainer = new Container();
    this.spriteContainer.name = "MatrixSpriteContainer";
    // Optimize container for many children
    this.spriteContainer.sortableChildren = false; // Disable sorting for performance
    this.addChild(this.spriteContainer);
    
    this.sprites = [];
    this.grid = [];
    this.streams = [];

    this.initialize();
  }

  /**
   * Initialize texture cache for all characters (called once, shared across instances)
   * Textures are generated on-demand via getCharTexture() for better performance
   */
  private static initializeTextureCache(): void {
    // Textures are generated on-demand in getCharTexture() method
    // This avoids blocking initialization and allows lazy loading
  }
  
  /**
   * Get texture for a character (from cache, or generate on-demand)
   */
  private static getCharTexture(char: string): Texture {
    // Handle empty character
    if (!char || char === '') {
      const emptyTexture = MatrixBackground.charTextureCache.get(' ');
      if (emptyTexture) {
        return emptyTexture;
      }
      // Fallback: use space character
      char = ' ';
    }
    
    let texture = MatrixBackground.charTextureCache.get(char);
    
    if (!texture) {
      // Generate texture on-demand if not in cache
      // Create a canvas to render the text
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d', { willReadFrequently: false });
      
      if (!context) {
        // Fallback to a default texture if context creation fails
        const fallbackTexture = MatrixBackground.charTextureCache.get('M');
        return fallbackTexture || Texture.EMPTY;
      }
      
      // Set font and measure first to get proper dimensions
      const fontString = `${MatrixBackground.FONT_WEIGHT} ${MatrixBackground.FONT_SIZE}px ${MatrixBackground.FONT_FAMILY}`;
      context.font = fontString;
      context.textBaseline = 'top';
      context.textAlign = 'left';
      
      // Measure text - use actualBoundingBox for better accuracy
      const metrics = context.measureText(char);
      const padding = 2;
      // Use actualBoundingBox if available, otherwise fallback to width
      const textWidth = metrics.actualBoundingBoxRight ? 
        metrics.actualBoundingBoxRight - (metrics.actualBoundingBoxLeft || 0) : 
        (metrics.width || MatrixBackground.FONT_SIZE);
      const textHeight = metrics.actualBoundingBoxAscent ? 
        metrics.actualBoundingBoxAscent + (metrics.actualBoundingBoxDescent || 0) : 
        MatrixBackground.FONT_SIZE * 1.2;
      
      // Ensure minimum size
      const width = Math.max(Math.ceil(textWidth) + padding * 2, MatrixBackground.FONT_SIZE);
      const height = Math.max(Math.ceil(textHeight) + padding * 2, MatrixBackground.FONT_SIZE);
      
      // Set canvas size (this resets the context, so we need to reconfigure)
      canvas.width = width;
      canvas.height = height;
      
      // Reconfigure context after canvas resize
      context.font = fontString;
      context.textBaseline = 'top';
      context.textAlign = 'left';
      // Use white color for textures so tinting works correctly
      // Tinting multiplies the texture color, so white allows full color control
      context.fillStyle = '#ffffff';
      
      // Calculate text position to center it better
      const x = padding + (metrics.actualBoundingBoxLeft ? -metrics.actualBoundingBoxLeft : 0);
      const y = padding;
      
      // Draw the text in white (will be tinted to green/other colors later)
      try {
        context.fillText(char, x, y);
      } catch (e) {
        // If text rendering fails, use fallback character
        console.warn(`Failed to render character "${char}", using fallback`);
        const fallbackTexture = MatrixBackground.charTextureCache.get('M');
        if (fallbackTexture) {
          return fallbackTexture;
        }
        // Draw fallback character
        context.fillText('M', x, y);
      }
      
      // Create texture from canvas
      try {
        texture = Texture.from(canvas);
        MatrixBackground.charTextureCache.set(char, texture);
      } catch (e) {
        console.warn(`Failed to create texture for character "${char}"`);
        // Return fallback texture
        const fallbackTexture = MatrixBackground.charTextureCache.get('M');
        return fallbackTexture || Texture.EMPTY;
      }
    }
    
    return texture || Texture.EMPTY;
  }

  private initialize(): void {
    // Calculate padding offset to align sprites with visible area
    // The container is positioned at (-padding, -padding), so we need to offset
    // sprites by padding amount to align with visible area starting at (0, 0)
    const paddingX = (this.backgroundWidth - this.visibleWidth) / 2;
    const paddingY = (this.backgroundHeight - this.visibleHeight) / 2;
    
    // Initialize grid
    for (let i = 0; i < this.columns; i++) {
      this.grid[i] = [];
      this.sprites[i] = [];
      
      for (let j = 0; j < this.rows + MatrixBackground.GRID_BUFFER_ROWS; j++) {
        this.grid[i][j] = {
          char: '',
          brightness: 0,
          age: 0,
          lastFill: undefined,
          lastAlpha: undefined,
          lastCharChange: undefined
        };
      }
      
      // Create sprites with cached textures for high performance
      // Offset by padding to align with visible area when container is positioned at (-padding, -padding)
      for (let j = 0; j < this.rows + MatrixBackground.SPRITE_BUFFER_ROWS; j++) {
        // Use space character as default texture (will be changed dynamically)
        const texture = MatrixBackground.getCharTexture(' ');
        const sprite = new Sprite(texture);
        sprite.x = paddingX + i * this.fontSize;
        sprite.y = paddingY + j * this.fontSize;
        sprite.visible = false;
        sprite.anchor.set(0, 0); // Top-left anchor for precise positioning
        sprite.scale.set(1, 1); // Ensure scale is 1:1
        this.spriteContainer.addChild(sprite);
        this.sprites[i][j] = sprite;
      }
      
      // Create streams
      this.streams.push({
        x: i,
        y: -Math.random() * this.rows,
        speed: Math.random() * MatrixBackground.STREAM_SPEED_MULTIPLIER + MatrixBackground.STREAM_SPEED_BASE,
        length: Math.floor(Math.random() * MatrixBackground.STREAM_LENGTH_RANGE) + MatrixBackground.STREAM_LENGTH_BASE,
        active: Math.random() > MatrixBackground.STREAM_INITIAL_ACTIVE_PROBABILITY
      });
    }
  }
  
  private getBackgroundWidth(): number {
    return this.backgroundGraphics ? (this.backgroundGraphics as any).width || 0 : 0;
  }
  
  private getBackgroundHeight(): number {
    return this.backgroundGraphics ? (this.backgroundGraphics as any).height || 0 : 0;
  }

  /**
   * Update the Matrix effect animation
   * Performance optimizations:
   * - Texture caching: Pre-generates and caches character textures to avoid repeated generation
   * - Sprite-based rendering: Uses Sprite objects with cached textures instead of Text objects
   * - Dirty checking: Only updates sprite properties when values actually change
   * - Cached values: Pre-calculates color constants and caches Math.random() results
   * - Conditional updates: Only sets visibility/texture/tint/alpha when needed
   * - Optimized loops: Caches array lengths and frequently accessed values
   */
  update(): void {
    const charsLength = this.chars.length;
    
    // Update streams
    for (let i = 0; i < this.streams.length; i++) {
      const stream = this.streams[i];
      
      if (!stream.active) {
        // Only check random once per inactive stream
        if (Math.random() > MatrixBackground.STREAM_ACTIVATION_PROBABILITY) {
          const rand1 = Math.random();
          const rand2 = Math.random();
          stream.active = true;
          stream.y = -stream.length;
          stream.speed = rand1 * MatrixBackground.STREAM_SPEED_MULTIPLIER + MatrixBackground.STREAM_SPEED_BASE;
          stream.length = Math.floor(rand2 * MatrixBackground.STREAM_LENGTH_RANGE) + MatrixBackground.STREAM_LENGTH_BASE;
        }
        continue;
      }

      stream.y += stream.speed;

      // Update grid cells for this stream
      const streamLength = stream.length;
      const streamX = stream.x;
      const streamY = stream.y;
      
      for (let j = 0; j < streamLength; j++) {
        const row = Math.floor(streamY - j);
        if (row >= 0 && row < this.rows + MatrixBackground.GRID_BUFFER_ROWS) {
          const cell = this.grid[streamX][row];
          
          // Only change character if enough time has passed since last change (prevents rapid changes)
          const minFramesBetweenChanges = 10; // Minimum frames between character changes
          const framesSinceLastChange = cell.lastCharChange !== undefined ? cell.age - cell.lastCharChange : Infinity;
          
          if (j === 0) {
            // Head of stream - only change character if enough time has passed
            if (framesSinceLastChange >= minFramesBetweenChanges || cell.lastCharChange === undefined) {
              cell.char = this.chars[Math.floor(Math.random() * charsLength)];
              cell.lastCharChange = 0;
            }
            cell.brightness = MatrixBackground.BRIGHTNESS_MAX;
            cell.age = 0;
          } else if (j < MatrixBackground.BRIGHT_CELL_THRESHOLD) {
            if (cell.brightness < MatrixBackground.BRIGHTNESS_BRIGHT_THRESHOLD) {
              if (framesSinceLastChange >= minFramesBetweenChanges || cell.lastCharChange === undefined) {
                cell.char = this.chars[Math.floor(Math.random() * charsLength)];
                cell.lastCharChange = 0;
              }
              cell.brightness = MatrixBackground.BRIGHTNESS_BRIGHT_THRESHOLD;
              cell.age = 0;
            }
          } else {
            const targetBrightness = MatrixBackground.BRIGHTNESS_MID_BASE - (j / streamLength) * MatrixBackground.BRIGHTNESS_MID_MULTIPLIER;
            if (cell.brightness < targetBrightness) {
              if (framesSinceLastChange >= minFramesBetweenChanges || cell.lastCharChange === undefined) {
                cell.char = this.chars[Math.floor(Math.random() * charsLength)];
                cell.lastCharChange = 0;
              }
              cell.brightness = targetBrightness;
              cell.age = 0;
            }
          }
        }
      }

      if (streamY > this.rows + streamLength) {
        stream.active = false;
        stream.y = -streamLength;
      }
    }

    // Age all cells and update sprites (optimized with dirty checking)
    for (let i = 0; i < this.columns; i++) {
      for (let j = 0; j < this.rows; j++) {
        const cell = this.grid[i][j];
        const sprite = this.sprites[i][j];
        
        if (cell.brightness > 0) {
          // Only update texture if character changed
          if (cell.char) {
            const newTexture = MatrixBackground.getCharTexture(cell.char);
            if (sprite.texture !== newTexture) {
              sprite.texture = newTexture;
            }
          }
          
          // Calculate color and alpha based on brightness
          let fillColor: number;
          let alpha: number;
          
          if (cell.brightness >= MatrixBackground.BRIGHTNESS_WHITE_THRESHOLD) {
            fillColor = MatrixBackground.COLOR_WHITE;
            alpha = MatrixBackground.ALPHA_MAX;
          } else if (cell.brightness >= MatrixBackground.BRIGHTNESS_BRIGHT_GREEN_THRESHOLD) {
            fillColor = MatrixBackground.COLOR_BRIGHT_GREEN;
            alpha = MatrixBackground.ALPHA_MAX;
          } else if (cell.brightness >= MatrixBackground.BRIGHTNESS_GREEN_THRESHOLD) {
            fillColor = MatrixBackground.COLOR_GREEN;
            alpha = MatrixBackground.ALPHA_MID;
          } else {
            fillColor = MatrixBackground.COLOR_DARK_GREEN;
            alpha = cell.brightness * MatrixBackground.ALPHA_DARK_MULTIPLIER;
          }
          
          // Only update tint if color changed (dirty checking)
          // Convert hex color to tint (0xFFFFFF = white tint, 0x000000 = black tint)
          // For ParticleContainer, we use tint property instead of style.fill
          const tint = fillColor;
          if (cell.lastFill !== tint) {
            sprite.tint = tint;
            cell.lastFill = tint;
          }
          if (cell.lastAlpha !== alpha) {
            sprite.alpha = alpha;
            cell.lastAlpha = alpha;
          }
          
          // Only make visible if not already visible
          if (!sprite.visible) {
            sprite.visible = true;
          }
          
          // Randomly change character occasionally
          if (cell.age > MatrixBackground.CHAR_CHANGE_AGE_THRESHOLD && Math.random() > MatrixBackground.CHAR_CHANGE_PROBABILITY) {
            const framesSinceLastChange = cell.lastCharChange !== undefined ? cell.age - cell.lastCharChange : Infinity;
            if (framesSinceLastChange >= 10) { // Also respect minimum frames between changes
              cell.char = this.chars[Math.floor(Math.random() * this.chars.length)];
              cell.lastCharChange = cell.age;
              // Force texture update since char changed
              sprite.texture = MatrixBackground.getCharTexture(cell.char);
            }
          }
          
          cell.age++;
          cell.brightness *= MatrixBackground.BRIGHTNESS_DECAY;
          
          if (cell.brightness < MatrixBackground.BRIGHTNESS_MIN) {
            cell.brightness = 0;
            cell.lastFill = undefined;
            cell.lastAlpha = undefined;
            sprite.visible = false;
          }
        } else {
          // Only hide if currently visible
          if (sprite.visible) {
            sprite.visible = false;
          }
        }
      }
    }
  }

  /**
   * Resize the Matrix background
   */
  resize(backgroundWidth: number, backgroundHeight: number, visibleWidth?: number, visibleHeight?: number): void {
    // Update stored dimensions
    this.backgroundWidth = backgroundWidth;
    this.backgroundHeight = backgroundHeight;
    
    // Update visible dimensions (use provided or fallback to background size)
    const newVisibleWidth = visibleWidth ?? backgroundWidth;
    const newVisibleHeight = visibleHeight ?? backgroundHeight;
    
    // Calculate columns and rows based on visible size (not padded size)
    const newColumns = Math.floor(newVisibleWidth / this.fontSize);
    const newRows = Math.floor(newVisibleHeight / this.fontSize);
    
    // Update visible dimensions
    this.visibleWidth = newVisibleWidth;
    this.visibleHeight = newVisibleHeight;
    
    // Update black background - use full size to cover entire area including padding
    this.backgroundGraphics.clear();
    this.backgroundGraphics.rect(0, 0, backgroundWidth, backgroundHeight);
    this.backgroundGraphics.fill(MatrixBackground.COLOR_BACKGROUND);
    // Ensure background stays at the bottom layer and is positioned correctly
    this.backgroundGraphics.zIndex = MatrixBackground.BACKGROUND_Z_INDEX;
    this.backgroundGraphics.x = 0;
    this.backgroundGraphics.y = 0;
    
    if (newColumns !== this.columns || newRows !== this.rows) {
      // Remove old sprites from sprite container
      if (this.spriteContainer) {
        this.spriteContainer.removeChildren();
      }
      
      // Update dimensions
      this.columns = newColumns;
      this.rows = newRows;
      
      // ParticleContainer automatically handles size, no manual resizing needed
      
      // Reinitialize sprites
      this.initialize();
      
      // Ensure background stays at the bottom after reinitialization
      if (this.backgroundGraphics.parent) {
        this.setChildIndex(this.backgroundGraphics, 0);
      }
    }
  }
}

